import classNames from 'classnames';
import { KeyboardEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { BasketItem } from '../../../../types/basket';
import { LOADING_TYPES, useCheckoutState } from '../../../../utils/checkoutState';
import { getAssetUrl } from '../../../../utils/config';
import debounce from '../../../../utils/debounce';
import {
  getGaEcommerceObject,
  pushErrorTrackingEventFromErrorObject,
  pushTrackingEvent
} from '../../../../utils/tracking';
import { showDefaultError } from '../../Notifications';
import './style.scss';

type QuantityProps = {
  item: BasketItem;
  maxDigits?: number;
};

const Quantity = ({ item, maxDigits = 2 }: QuantityProps) => {
  const { checkoutState, setBasketItemQuantity, setBasketEmpty, isLoading, setLoading } = useCheckoutState();
  const [value, setValue] = useState(item.quantity.toString());
  const hasFocus = useRef<boolean>(false);
  const maxQuantity =
    item.maximumBuyableQuantityPerOrder ? item.maximumBuyableQuantityPerOrder : Math.pow(10, maxDigits) - 1;

  const changeQuantity = async (newQuantity: number) => {
    setLoading(LOADING_TYPES.itemQuantityChange(item.id));
    try {
      const updatedQuantity = await setBasketItemQuantity(
        { id: item.id, quantity: newQuantity },
        { prevQuantity: item.quantity, ...(newQuantity === 0 && { removedBasketItem: item }) }
      );

      if (updatedQuantity && newQuantity !== updatedQuantity) {
        setValue(updatedQuantity.toString());
      }
    } catch (e: any) {
      setValue(item.quantity.toString());
      if (e.statusCode === 204) {
        pushTrackingEvent({
          event: 'remove_from_cart',
          action: `${window.location.hash}|${item.id}`,
          price_bi: null,
          ecommerce: getGaEcommerceObject(checkoutState, item.id, 1, item, true)
        });
        setBasketEmpty(true);
        return;
      }
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'set_basket_item_quantity');
    }

    setLoading(LOADING_TYPES.off);
    hasFocus.current = false;
  };

  const onFocus = () => (hasFocus.current = true);
  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const onBlur = () => {
    hasFocus.current = false;
    const numericValue = parseInt(value);
    if (!value || numericValue < 0) {
      setValue(item.quantity.toString());
      return;
    }
    if (numericValue === item.quantity) {
      return;
    }
    changeQuantity(numericValue);
  };

  const onChange = (nextValue: number) => {
    if (nextValue === 0 || !!isLoading || nextValue > maxQuantity) {
      return;
    }
    hasFocus.current = true;
    setValue(nextValue.toString());
    debouncedChange(nextValue);
  };

  const debouncedChange = useCallback(
    debounce((nextValue: unknown) => {
      changeQuantity(parseInt(nextValue as string));
    }, 500),
    [item.quantity]
  );

  useEffect(() => {
    const newQuantityString = item.quantity.toString();
    if (newQuantityString !== value) {
      setValue(newQuantityString);
    }
  }, [item.quantity]);

  return (
    <div className="quantity" data-test-id="item-quantity">
      <img
        className={classNames('quantity__img', {
          'cursor-disabled': !!isLoading,
          'quantity__img--grayed cursor-disabled': value === '1'
        })}
        onClick={() => onChange(parseInt(value) - 1)}
        src={getAssetUrl('basket/minus.svg')}
        data-test-id="item-quantity-minus"
      />
      <input
        type="number"
        inputMode="numeric"
        maxLength={maxDigits}
        className="quantity__input"
        value={value}
        onChange={({ currentTarget }) => {
          if (currentTarget.value.length > maxDigits) {
            return;
          }
          setValue(currentTarget.value);
        }}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        disabled={!!isLoading}
        data-test-id="item-quantity-value"
        data-cs-mask
        data-mf-replace="**REMOVED**"
      />
      <img
        className={classNames('quantity__img', {
          'cursor-disabled': !!isLoading || parseInt(value) === maxQuantity,
          'quantity__img--grayed cursor-disabled': parseInt(value) === maxQuantity
        })}
        onClick={() => onChange(parseInt(value) + 1)}
        src={getAssetUrl('basket/plus.svg')}
        data-test-id="item-quantity-plus"
      />
    </div>
  );
};

export default Quantity;
